import { Fragment, useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import Guess from './Guess';
import GuessHeadings from './GuessHeadings';
import Results from './Results';
import StateContext from '../../store/state-context';
import { getGame } from '../../lib/api';

const DEFAULT_COLUMN_CONFIG = {
	columns: [
		{ key: 'teams',        label: 'Team',  source: 'teams' },
		{ key: 'states',       label: 'State', source: 'states' },
		{ key: 'positions',    label: 'Pos.',  source: 'positions' },
		{ key: 'numbers',      label: 'No.',   source: 'numbers' },
		{ key: 'career_games', label: 'Games', source: 'career_games' },
		{ key: 'age',          label: 'Age',   source: 'age' },
	],
	game_team_code: null,
};

const flagDuplicateOptions = (options) =>
	options.map((opt, i) => ({
		...opt,
		isDuplicate:
			(options[i + 1]?.name === opt.name) ||
			(options[i - 1]?.name === opt.name),
	}));

const Board = (props) => {
	const [ isLoading, setIsLoading ] = useState(true);
	const [ isGameToday, setIsGameToday ] = useState(true);
	const [ state, setState ] = useState();
	const [ stats, setStats ] = useState();

	const gameTypeSlug = props.gameTypeSlug;
	const gameTypeName = props.gameTypeName;
	const STATUS_IN_PROGRESS = props.STATUS_IN_PROGRESS;
	const STATUS_WON = props.STATUS_WON;
	const STATUS_LOST = props.STATUS_LOST;
	const showResults = props.showResults;
	const setShowResults = props.setShowResults;

	const [ optionsOpen, setOptionsOpen ] = useState(false);
	const [ error, setError ] = useState(false);
	const [ isRetrying, setIsRetrying ] = useState(false);
	const [ value, setValue ] = useState(null);
	const [ inputValue, setInputValue ] = useState('');

	const stateCtx = useContext(StateContext);

	const game_id = state ? state.game_id : null;
	const answer = state ? state.answer : null;
	const expiryTs = state ? state.expiryTs : null;
	const options = state ? state.options : [];
	const status = state ? state.status : null;
	const guesses = state ? state.guesses : [];
	const columnConfig = (state && state.columnConfig) || DEFAULT_COLUMN_CONFIG;
	const gameTeamCode = columnConfig.game_team_code;
	const maxGuesses = (state && state.max_guesses) || props.MAX_GUESSES;

	useEffect(
		() => {
			setError(false);
			setIsGameToday(true);
			setIsLoading(true);
			setState(null);

			const stateHistory = stateCtx.retrieveState(gameTypeSlug);
			setState(stateHistory);
			if (stateHistory?.max_guesses)  props.onMaxGuessesChange?.(stateHistory.max_guesses);
			if (stateHistory?.columnConfig) props.onColumnConfigChange?.(stateHistory.columnConfig);

			const statsHistory = stateCtx.retrieveStats(gameTypeSlug);
			setStats(statsHistory);

			const loadedExpiryTs = stateHistory ? stateHistory.expiryTs : null;
			const isGameExpired = loadedExpiryTs ? loadedExpiryTs < +new Date() / 1000 : true;

			if (!isGameExpired) {
				setIsLoading(false);
				props.onLoadingChange?.(false);
				return;
			}

			setState(null);

			const controller = new AbortController();
			let cancelled = false;

			getGame(gameTypeSlug, controller.signal, { onRetry: () => setIsRetrying(true) })
				.then((data) => {
					if (cancelled) return;
					if (!data.answer) {
						setIsGameToday(false);
					} else {
						setState({
							game_id: data.game_id,
							answer: data.answer,
							expiryTs: data.expiry_timestamp,
							options: data.options,
							columnConfig: data.game_type?.column_config,
							max_guesses: data.game_type?.max_guesses,
							status: STATUS_IN_PROGRESS,
							guesses: []
						});
						if (data.game_type?.max_guesses)    props.onMaxGuessesChange?.(data.game_type.max_guesses);
						if (data.game_type?.column_config)  props.onColumnConfigChange?.(data.game_type.column_config);
					}
				})
				.catch((err) => {
					if (cancelled) return;
					if (err.name === 'AbortError') return;
					setError(err.message || 'Something went wrong!');
					setIsGameToday(false);
				})
				.finally(() => {
					if (!cancelled) {
						setIsRetrying(false);
						setIsLoading(false);
						props.onLoadingChange?.(false);
					}
				});

			return () => {
				cancelled = true;
				controller.abort();
			};
		},
		[ gameTypeSlug, stateCtx, STATUS_IN_PROGRESS ]
	);

	const handleGuessSubmission = (event, guess) => {
		const guessNo = guesses.length + 1;

		// check if answer is correct
		// if correct, open popup with "You Beauty", player details, and stats - save stats to localStorage
		let outcomeStatus;
		if (guess.player_id === answer.player_id) {
			outcomeStatus = STATUS_WON;
		} else if (guessNo === maxGuesses) {
			outcomeStatus = STATUS_LOST;
		}

		//
		// Evaluation logic
		// Attributes (9): Match, Teams, States, Positions, Numbers, Height, Games, Age, Origin, Draft Years, Debut Year
		//
		let evaluation = {
			overall: { result: 0, higher: null },
			teams: { result: 0, higher: null },
			states: { result: 0, higher: null },
			positions: { result: 0, higher: null },
			numbers: { result: 0, higher: null },
			height: { result: 0, higher: null },
			games: { result: 0, higher: null },
			age: { result: 0, higher: null },
			origin: { result: 0, higher: null },
			draft_years: { result: 0, higher: null },
			debut_year: { result: 0, higher: null },
			other_teams: { result: 0, higher: null },
			club_numbers: { result: 0, higher: null },
			club_games: { result: 0, higher: null },
			club_debut: { result: 0, higher: null },
		};

		// Evaluate overall match
		if (outcomeStatus === STATUS_WON) evaluation.overall.result = 2;

		// Evaluate teams
		const guessTeams = guess.teams.split(';');
		const answerTeams = answer.teams.split(';');
		if (guessTeams[0] === answerTeams[0]) {
			evaluation.teams.result = 2;
		} else {
			for (const team of answerTeams) {
				if (team === guessTeams[0]) {
					evaluation.teams.result = 1;
					break;
				}
			}
		}

		// Evaluate states
		const guessStates = guess.states.split(';');
		const answerStates = answer.states.split(';');
		if (guessStates[0] === answerStates[0]) {
			evaluation.states.result = 2;
		} else {
			for (const state of answerStates) {
				if (state === guessStates[0]) {
					evaluation.states.result = 1;
					break;
				}
			}
		}

		// Evaluate positions
		const guessPositions = guess.positions.split('/');
		const answerPositions = answer.positions.split('/');
		if (guess.positions === answer.positions) {
			evaluation.positions.result = 2;
		} else {
			for (const guessPosition of guessPositions) {
				for (const answerPosition of answerPositions) {
					if (guessPosition === answerPosition) {
						evaluation.positions.result = 1;
						break;
					}
				}
				if (evaluation.positions.result > 0) break;
			}
		}

		// Evaluate numbers
		const guessNumbers = guess.numbers.split(';');
		const answerNumbers = answer.numbers.split(';');
		if (guessNumbers[0] === answerNumbers[0]) {
			evaluation.numbers.result = 2;
		} else {
			for (const number of answerNumbers) {
				if (number === guessNumbers[0]) {
					evaluation.numbers.result = 1;
					break;
				}
			}
      evaluation.numbers.higher = +guessNumbers[0] < +answerNumbers[0] ? true : false;
		}

		// Evaluate height
		const TOLERANCE_HEIGHT = 5;
		if (guess.height === answer.height) {
			evaluation.height.result = 2;
		} else {
      if (
        (guess.height < answer.height && guess.height + TOLERANCE_HEIGHT >= answer.height) ||
        (guess.height > answer.height && guess.height - TOLERANCE_HEIGHT <= answer.height)
      ) {
        evaluation.height.result = 1;
      }
      evaluation.height.higher = guess.height < answer.height ? true : false;
    }

		// Evaluate games
		const TOLERANCE_GAMES = 50;
		if (guess.career_games === answer.career_games) {
			evaluation.games.result = 2;
		} else {
      if (
        (guess.career_games < answer.career_games && guess.career_games + TOLERANCE_GAMES >= answer.career_games) ||
        (guess.career_games > answer.career_games && guess.career_games - TOLERANCE_GAMES <= answer.career_games)
      ) {
        evaluation.games.result = 1;
      }
      evaluation.games.higher = guess.career_games < answer.career_games ? true : false;
    }

		// Evaluate age
		const TOLERANCE_AGE = 2;
		if (guess.age === answer.age) {
			evaluation.age.result = 2;
		} else {
      if (
        (guess.age < answer.age && guess.age + TOLERANCE_AGE >= answer.age) ||
        (guess.age > answer.age && guess.age - TOLERANCE_AGE <= answer.age)
      ) {
        evaluation.age.result = 1;
      }
      evaluation.age.higher = guess.age < answer.age ? true : false;
    }
    
		// Evaluate origin
		const guessOrigin = guess.origin;
		const answerOrigin = answer.origin;
		if (guessOrigin && answerOrigin) {
			if (guessOrigin === answerOrigin) {
				evaluation.origin.result = 2;
			}
		}

		// Evaluate draft years
		const guessDraftYears = guess.draft_years && guess.draft_years.split(';');
		const answerDraftYears = answer.draft_years && answer.draft_years.split(';');
		if (guessDraftYears && answerDraftYears) {
			if (guessDraftYears[0] === answerDraftYears[0]) {
				evaluation.draft_years.result = 2;
			}
		}

		// Evaluate debut year
		const TOLERANCE_DEBUT_YEAR = 2;
		const guessDebutYear = guess.debut_year;
		const answerDebutYear = answer.debut_year;
		if (guessDebutYear && answerDebutYear) {
			if (guessDebutYear === answerDebutYear) {
				evaluation.debut_year.result = 2;
			} else {
        if (
          (guess.debut_year < answer.debut_year &&
            guess.debut_year + TOLERANCE_DEBUT_YEAR >= answer.debut_year) ||
          (guess.debut_year > answer.debut_year && guess.debut_year - TOLERANCE_DEBUT_YEAR <= answer.debut_year)
        ) {
          evaluation.debut_year.result = 1;
        }
        evaluation.debut_year.higher = guess.debut_year < answer.debut_year ? true : false;
      }
		}

		// Evaluate other_teams
		if (gameTeamCode) {
			const guessOtherTeams = guess.teams.split(';').filter(t => t !== gameTeamCode);
			const answerOtherTeams = answer.teams.split(';').filter(t => t !== gameTeamCode);
			if ([...guessOtherTeams].sort().join() === [...answerOtherTeams].sort().join()) {
				evaluation.other_teams.result = 2;
			} else if (guessOtherTeams.some(t => answerOtherTeams.includes(t))) {
				evaluation.other_teams.result = 1;
			}
		}

		// Evaluate club_stats fields (find the entry for the subject team)
		const guessClubEntry  = guess.club_stats?.find(s => s.team === gameTeamCode);
		const answerClubEntry = answer.club_stats?.find(s => s.team === gameTeamCode);

		if (guessClubEntry && answerClubEntry) {
			// club_numbers (no tolerance, directional arrow)
			const gNum = guessClubEntry.numbers?.[0];
			const aNum = answerClubEntry.numbers?.[0];
			if (gNum != null && aNum != null) {
				if (gNum === aNum) {
					evaluation.club_numbers.result = 2;
				} else {
					if (answerClubEntry.numbers.includes(gNum)) evaluation.club_numbers.result = 1;
					evaluation.club_numbers.higher = gNum < aNum;
				}
			}

			// club_games (±50 tolerance)
			const TOLERANCE_CLUB_GAMES = 50;
			const gGames = guessClubEntry.games;
			const aGames = answerClubEntry.games;
			if (gGames != null && aGames != null) {
				if (gGames === aGames) {
					evaluation.club_games.result = 2;
				} else {
					if (Math.abs(gGames - aGames) <= TOLERANCE_CLUB_GAMES) evaluation.club_games.result = 1;
					evaluation.club_games.higher = gGames < aGames;
				}
			}

			// club_debut — years[0] is the debut year at the club (±2 tolerance)
			const TOLERANCE_CLUB_DEBUT = 2;
			const gDebut = guessClubEntry.years?.[0];
			const aDebut = answerClubEntry.years?.[0];
			if (gDebut != null && aDebut != null) {
				if (gDebut === aDebut) {
					evaluation.club_debut.result = 2;
				} else {
					if (Math.abs(gDebut - aDebut) <= TOLERANCE_CLUB_DEBUT) evaluation.club_debut.result = 1;
					evaluation.club_debut.higher = gDebut < aDebut;
				}
			}
		}

		setState((prevState) => {
			const newState = {
				...prevState,
				options: prevState.options.filter((option) => option.player_id !== guess.player_id),
				status: outcomeStatus ? outcomeStatus : prevState.status,
				guesses: [ ...prevState.guesses, { ...guess, evaluation } ]
			};
			stateCtx.updateState(gameTypeSlug, newState);
			return newState;
		});

		setValue(null);
		setInputValue('');

		if (outcomeStatus) {
			setStats((prevStats) => {
				const didWin = outcomeStatus === STATUS_WON;

				const emptyGuessesHistoryObj = {};
				for (let i = 1; i <= maxGuesses; i++) {
					emptyGuessesHistoryObj[i] = 0;
				}

				const newGamesPlayed = prevStats ? prevStats.gamesPlayed + 1 : 1;
				const newGamesWon = prevStats ? prevStats.gamesWon + (didWin ? 1 : 0) : didWin ? 1 : 0;
				const newGamesWonPercentage = newGamesPlayed > 0 ? newGamesWon / newGamesPlayed : null;
				const newGuessesHistory = prevStats
					? { ...prevStats.guessesHistory, [guessNo]: prevStats.guessesHistory[guessNo] + 1 }
					: { ...emptyGuessesHistoryObj, [guessNo]: 1 };
				const newStreakCurrent = prevStats ? prevStats.streakCurrent + (didWin ? 1 : 0) : didWin ? 1 : 0;
				const newStreakMax = prevStats ? Math.max(newStreakCurrent, prevStats.streakMax) : newStreakCurrent;

				let count = 0;
				let total = 0;
				for (let [ key, value ] of Object.entries(newGuessesHistory)) {
					count = count + value;
					total = total + key * value;
				}
				const newGuessesAverage = total / count;

				const newStats = {
					...prevStats,
					gamesPlayed: newGamesPlayed,
					gamesWon: newGamesWon,
					gamesWonPercentage: newGamesWonPercentage,
					guessesHistory: newGuessesHistory,
					guessesAverage: newGuessesAverage,
					streakCurrent: newStreakCurrent,
					streakMax: newStreakMax
				};

				stateCtx.updateStats(gameTypeSlug, newStats);
				return newStats;
			});

			document.activeElement?.blur();
			setShowResults(true);
		}
	};

	const handleOptionsOpen = () => {
		if (inputValue.length > 0) setOptionsOpen(true);
	};

	const handleInputChange = (event, value) => {
		setInputValue(value);
		if (value.length > 0) {
			setOptionsOpen(true);
		} else {
			setOptionsOpen(false);
		}
	};

	const loadingContent = (
		<Paper align="center" sx={{ p: 10 }}>
			<CircularProgress />
			{isRetrying && (
				<Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
					Connecting...
				</Typography>
			)}
		</Paper>
	);
	const errorContent = (
		<Paper align="center" sx={{ p: 10 }}>
			{error}
		</Paper>
	);
	const noGameTodayContent = (
		<Paper align="center" sx={{ p: 10 }}>
			No Game Today
		</Paper>
	);

	const guessesContent = guesses.map((guess, index) => {
		return (
			<Guess
				key={guess.player_id}
				correct={guess.player_id === answer.player_id}
				guessNo={index + 1}
				guess={guess}
				columns={columnConfig.columns}
				gameTeamCode={gameTeamCode}
			/>
		);
	});

	const isFinished = status && status !== STATUS_IN_PROGRESS;
	const isCorrect = status === STATUS_WON;
	const placeholder = isFinished ? 'Game Over' : 'Guess ' + (guesses.length + 1) + ' of ' + maxGuesses;

	return (
		<Fragment>
			{ReactDOM.createPortal(
				<Results
					gameTypeName={gameTypeName}
					stats={stats}
					open={showResults}
					guesses={guesses}
					maxGuesses={maxGuesses}
					game_id={game_id}
					answer={answer}
					completed={isFinished}
					correct={isCorrect}
					isGameToday={isGameToday}
					nextGameTs={expiryTs + 1}
					columnConfig={columnConfig}
					onClose={() => setShowResults(false)}
				/>,
				document.getElementById('overlays-root')
			)}
			<Container>
				<Stack direction="column" spacing={1} pb={1}>
					{isLoading && !state && loadingContent}
					{error && !isLoading && errorContent}
					{!isGameToday && !error && !isLoading && noGameTodayContent}
					{isGameToday &&
					!error &&
					!isLoading && (
						<Fragment>
							<Autocomplete
								id="guess"
								value={value}
								inputValue={inputValue}
								open={optionsOpen}
								onOpen={handleOptionsOpen}
								onClose={() => setOptionsOpen(false)}
								onChange={handleGuessSubmission}
								onInputChange={handleInputChange}
								getOptionKey={(option) => option.player_id}
								isOptionEqualToValue={(option, value) =>
                option.name ? option.player_id === value.player_id : ''}
								getOptionLabel={(option) => {
									let label = option.name ? option.name : '';
									if (option.isDuplicate) {
										label =
											label +
											' (' +
											option.teams.split(';')[0] +
											' #' +
											option.numbers.split(';')[0] +
											')';
									}
									return label;
								}}
								filterOptions={(options, { inputValue }) => {
									const input = inputValue.trim().toLowerCase();
									if (!input) return [];
									return options.filter(option => {
										const name = option.name ? option.name.toLowerCase() : '';
										return name.startsWith(input) || name.split(' ').some(part => part.startsWith(input));
									});
								}}
								options={flagDuplicateOptions(options)}
								loading={isLoading}
								disabled={isFinished}
								renderInput={(params) => (
									<TextField
										{...params}
                    autoFocus
										placeholder={placeholder}
										sx={{
											'& .MuiOutlinedInput-root': {
												'&.Mui-focused fieldset': { borderColor: 'success.main' }
											}
										}}
										InputProps={{
											...params.InputProps,
											type: 'search',
											endAdornment: (
												<Fragment>
													<InputAdornment position="end">
														<Chip label={gameTypeName} size="small" sx={{ mr: 0.5, borderRadius: '4px', backgroundColor: 'primary.main', color: 'primary.contrastText' }} />
													</InputAdornment>
													{params.InputProps.endAdornment}
												</Fragment>
											)
										}}
									/>
								)}
							/>
							<GuessHeadings columns={columnConfig.columns} />
							{guessesContent}
						</Fragment>
					)}
				</Stack>
			</Container>
		</Fragment>
	);
};

export default Board;
